import {ReactElement} from "react";
import {useQuery} from "@apollo/client";
import classnames from "classnames/bind";

import {P, P1, Span1, Span3} from "../../../components/text";
import {Card} from "../../../components/card";
import {Button, InputRow, Separator} from "../../../components/input";
import {useMutationToast} from "../../../toast";
import {useMyUser, GET_SLACK_INFO, DISCONNECT_SLACK, SlackInfo} from "../../../data";
import {Icon} from "../../../components/images";
import {SSOModal} from "./sso";
import {TeamsCard} from "./teams";
import {SalesforceCard} from "./salesforce";
import {useNewModal as useModal} from "../../../modals";

import styles from "./integrations.module.scss";
const bStyles = classnames.bind(styles);

export const Integrations = (): ReactElement => {
	const {open: ssoOpen, modal: ssoModal} = useModal({});
	const {org, role} = useMyUser();
	const {data: slack} = useQuery<{slackInfo: SlackInfo}>(GET_SLACK_INFO);
	const [disconnectSlack, {loading: slackDisconnecting}] = useMutationToast(DISCONNECT_SLACK);

	return (
		<div>
			<h3>Integrations</h3>
			<Span1>
				Simplify communication, manage notifications, and unify your platforms with these key integrations.
			</Span1>
			<Separator horizontal />
			{role === "admin" && (
				<>
					<P1 bold>Company</P1>
					<div className={bStyles("integrationsRow")}>
						<div>
							<TeamsCard />
						</div>
						{org?.features?.salesforceLeadsSync && (
							<div>
								<SalesforceCard />
							</div>
						)}
						<div>
							{org.features.sso && (
								<Card className={bStyles("integrationsCard")}>
									<h5 className="space">Single Sign-On</h5>
									<P color="grey">
										Single sign-on (SSO) is an authentication process where users can log in to multiple
										applications from different devices using a single set of credentials.{" "}
										<a
											href="https://cvssupport.wpenginepowered.com/article/single-sign-on-with-clearview-social"
											target="_blank"
											rel="noreferrer"
										>
											Learn More
										</a>{" "}
										about SSO.
									</P>
									<Button invert onClick={ssoOpen} icon="settings" value="Configure" />
								</Card>
							)}
						</div>
					</div>
					<P1 bold>Personal</P1>
				</>
			)}
			<div className={bStyles("integrationsRow")}>
				<div>
					<Card className={bStyles("integrationsCard")}>
						<InputRow position="between">
							<div className={bStyles("header", "space")}>
								<Icon icon="slack-multicolor" width={32} />
								<h5 className="space">Slack</h5>
							</div>
							{slack?.slackInfo.connected && (
								<Button
									color="black"
									invert
									border={false}
									onClick={disconnectSlack}
									loading={slackDisconnecting}
									value="Remove account"
								/>
							)}
						</InputRow>

						<P color="grey">
							Seamlessly integrate with Slack to enhance team collaboration and bring Clearview Social’s
							powerful features and functionalities right to your workspace.
						</P>
						{slack?.slackInfo.connected ? (
							<div className={bStyles("centeredContent", "space")}>
								<Icon icon="circle-check" />
								<Span3>You’re connected</Span3>
							</div>
						) : (
							<Button
								onClick={() =>
									window.open(
										`${slack?.slackInfo.authUrl}?user_scope=${slack?.slackInfo.scope}&client_id=${slack?.slackInfo.clientId}&state=${slack?.slackInfo.state}&redirect_uri=${slack?.slackInfo.redirectUrl}`,
										"_self"
									)
								}
								value="Connect"
							/>
						)}
					</Card>
				</div>
				<div>
					<Card className={bStyles("integrationsCard")}>
						<h5 className="space">Share via Bookmark</h5>
						<P color="grey">
							Share or add articles while you’re browsing the web by adding the Clearview Social Sharing
							Bookmark to your browser.
						</P>
						<P>
							<a
								href="https://cvssupport.wpenginepowered.com/article/using-the-clearview-social-bookmarks"
								target="_blank"
								rel="noreferrer"
							>
								Learn More
							</a>
						</P>
					</Card>
				</div>
			</div>
			<SSOModal modal={ssoModal} />
		</div>
	);
};
